/**
 * **节点头部动作的宿主实现**（2027 加）。
 *
 * ## 分工（为什么声明与实现分在两处）
 *
 * ```text
 *   NodeTypeDef.actions   ← 包 / 类型声明"这里要有个按钮"（id + 图标 + 一句话）
 *   NODE_ACTIONS（这里）  ← 宿主回答"这个 id 干什么"
 * ```
 *
 * 用户要求这套东西**抽象在节点层**（"不是只为 js 代码节点添加这个功能"）：
 * 于是任何类型都能声明按钮，而**实现留在宿主** —— 因为"打开属性面板"
 * 要切窗口 + 设活动节点，那是宿主的知识（内核不认识窗口）。
 *
 * ⚠️ 抽成独立文件的**真正理由**是可断言：`verify:packages` 要遍历注册表里
 * 所有类型的 `actions`，检查每个 id 都在这张表里 —— 表留在 `Playground.vue`
 * 里的话，测试拿不到它，"声明了没人实现"就只能靠人碰巧点到那个按钮
 * 才发现（而它的表现恰好是"点了没反应"，最难归因的一类）。
 */
import type { NodeActionDef } from '@/node/package'

/** 一个动作的宿主实现 */
export interface NodeActionRunner {
  /** 面板 / 提示里用的名字 */
  label: string
  run: (nodeId: string) => void
}

/** 宿主提供的能力（动作实现要用到的东西，一律注入） */
export interface NodeActionCtx {
  /** 把这个节点设成活动节点（属性面板跟随它） */
  activate: (nodeId: string) => void
  /** 显示「属性」窗口（已有就聚焦、没有就切一个出来） */
  revealProperties: () => void
}

/**
 * 内核约定的动作 id。
 *
 * ⚠️ 这是**约定**不是枚举：域包可以自己加 id，但得由装了它的宿主提供实现。
 * 这里列的这几个是"任何宿主都该认识"的通用动作。
 */
export const KNOWN_NODE_ACTIONS = ['settings'] as const

export function createNodeActions(ctx: NodeActionCtx): Record<string, NodeActionRunner> {
  return {
    /**
     * **打开这个节点的属性面板**（通用动作）。
     *
     * 顺序有讲究：先把这个节点设成活动节点，再显示属性窗口 ——
     * 反过来的话，属性面板会先按"上一个活动节点"渲染一帧，看着像点错了。
     */
    settings: {
      label: '属性',
      run: (nodeId) => {
        ctx.activate(nodeId)
        ctx.revealProperties()
      },
    },
  }
}

/** 类型声明的动作 → 有没有实现（没有的那条要在注册时就指出来） */
export function missingNodeActions(
  actions: readonly NodeActionDef[] | undefined,
  known: readonly string[] = KNOWN_NODE_ACTIONS,
): string[] {
  if (!actions?.length) return []
  return actions.map((a) => a.id).filter((id) => !known.includes(id))
}
